// g2-baseline-exception-mark.ts
//
// Spec §8 condition 3 for G2: "baseline exception". P16 reported this as
// unenforced on the grounds that "ticket-schema.ts has no field for it" —
// true, but looking in the wrong place. The signal has existed since P8:
// `baseline_exceptions[]` in verification-schema.ts, documented verbatim (in
// the now-removed baseline-diffing module, R-25) as "test 9 — G2 approval
// via verification-profile's `baseline_exceptions`". The gate simply never
// read it.
//
// An accepted baseline exception is a person's written record that this
// mission is knowingly running with a red test. That is exactly the situation
// §8 wants a human to look at the plan for, so its presence turns G2 on.
//
// ⚠️ PRECISION about what that record does TODAY. "Excused" is the spec's
// intent, not yet the verifier's behaviour: the baseline-diffing module that
// once took `approvedExceptionNames` and was documented as the place
// exceptions get honoured had ZERO production callers, and was removed
// (R-25). `loadApprovedRecipes` (supervisor-attempt-cycle.ts) reads this same
// profile and takes only `profile.recipes`, ignoring `baseline_exceptions`
// entirely. So this gate is the ONLY production consumer of the field, and
// declaring an exception buys a worker nothing at verify time.
//
// Consequence for whoever wires the "real" excuse later: the gate must keep
// reading the declaration BEFORE the excuse can take effect. Wire it the other
// way round and a worker gets its red test forgiven by a mechanism the gate
// never saw — the 0-caller class of bug, mirrored.
//
// THE CATCH, and why this file exists at all: `verification-profile.json` lives
// INSIDE the worker's git worktree (supervisor-attempt-cycle.ts resolves it
// from `repoRoot`), and it is in neither MISSION_IGNORE_ENTRIES nor
// CONTROLLER_OWNED_PATH_GLOBS. So the party the gate governs can edit the
// input to the gate's decision — the Bug K/Bug L shape, one more time. Reading
// the live count each tick would let a worker declare an exception and then
// delete the entry again before the gate ever looks at it.
//
// So the observation is made DURABLE the same way the plan-size mark closed
// Bug L: record the peak, judge against the peak. Deleting an exception later
// cannot lower it, which removes the only thing deletion was good for. The
// property this needed — and that a self-declared `risk` field could never
// have — is that the worker cannot walk the signal back down once the
// controller has seen it.
//
// Safety property (identical to g2-plan-size-high-water-mark.ts): this value
// can only ever ADD a reason to require G2, never remove one. Every failure
// path returns 0 — an inert value — so a missing or damaged profile degrades
// to pre-fix behaviour, never to something laxer.
//
// Stored in the mission control root, outside the worker's worktree.
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { writeFileDurable } from "./durable-file-writer.js";
import { VerificationProfileSchema } from "./schemas/verification-schema.js";

const MARK_FILE_NAME = "g2-baseline-exception-mark.json";

function markPath(controlRoot: string): string {
  return join(controlRoot, MARK_FILE_NAME);
}

/**
 * Counts `baseline_exceptions` declared in the execution repo's
 * verification-profile. Returns 0 when the file is absent, unparseable, or
 * fails schema validation — see the safety note above: an unreadable profile
 * must never read as "fewer exceptions than we already saw", it just adds
 * nothing new this tick.
 */
export async function countDeclaredBaselineExceptions(repoDir: string): Promise<number> {
  let raw: string;
  try {
    raw = await readFile(join(repoDir, ".avatar", "verification-profile.json"), "utf8");
  } catch {
    return 0;
  }
  try {
    const profile = VerificationProfileSchema.parse(JSON.parse(raw));
    return profile.baseline_exceptions.length;
  } catch {
    return 0;
  }
}

/**
 * Most baseline exceptions ever observed for this mission. Returns 0 when
 * absent or unreadable — 0 cannot trip the condition, so a damaged mark never
 * grants a waiver.
 */
export async function readG2BaselineExceptionMark(controlRoot: string): Promise<number> {
  let raw: string;
  try {
    raw = await readFile(markPath(controlRoot), "utf8");
  } catch {
    return 0;
  }
  try {
    const parsed = JSON.parse(raw) as { baseline_exception_count?: unknown };
    const count = parsed.baseline_exception_count;
    if (typeof count !== "number" || !Number.isFinite(count) || count < 0) return 0;
    return Math.floor(count);
  } catch {
    return 0;
  }
}

/**
 * Raises the mark to `observedCount` when that is larger. A smaller
 * observation is IGNORED — that is what makes deleting an exception from the
 * worktree useless as a way to shed the condition.
 */
export async function recordG2BaselineExceptionMark(
  controlRoot: string,
  observedCount: number,
): Promise<void> {
  const current = await readG2BaselineExceptionMark(controlRoot);
  if (observedCount <= current) return;
  await writeFileDurable(
    markPath(controlRoot),
    Buffer.from(
      `${JSON.stringify({ schema_version: 1, baseline_exception_count: observedCount }, null, 2)}\n`,
      "utf8",
    ),
  );
}
